import { TenantConfig } from '@/types/tenant';
import { applyTheme } from './themes';
import { companyService } from '@/services/company.service';

/**
 * Get fallback tenant configuration for offline mode or errors
 */
export function getFallbackTenantConfig(slug?: string): TenantConfig {
  const companySlug = slug || 'default-company';
  const companyName = slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Real Estate Company';

  return {
    id: 'fallback',
    name: companyName,
    slug: companySlug,
    logo: `/assets/${companySlug}/logo.svg`,
    favicon: `/assets/${companySlug}/favicon.ico`,
    primaryColor: '#7c3aed',
    secondaryColor: '#a855f7',
    accentColor: '#06b6d4',
    contact: {
      phone: '',
      email: '',
      address: '',
      whatsapp: ''
    },
    social: {
      facebook: '',
      instagram: '',
      linkedin: '',
      youtube: ''
    },
    seo: {
      title: `${companyName} - Real Estate`,
      description: `Find your perfect property with ${companyName}`,
      keywords: ['real estate', 'properties'],
      ogImage: `/assets/${companySlug}/og-image.jpg`
    },
    features: {
      blog: true,
      multiLanguage: false,
      propertyComparison: true,
      virtualTours: true
    },
    currencies: ['USD'],
    languages: ['es'],
    theme: {
      mode: 'light',
      style: 'modern'
    }
  };
}

/**
 * Legacy function for backward compatibility
 * Now uses the company service to fetch configuration
 */
export async function getTenantConfig(slug?: string): Promise<TenantConfig> {
  try {
    const tenantSlug = slug || companyService.getDefaultCompanySlug();
    return await companyService.getCompanyBySlug(tenantSlug);
  } catch (error) {
    console.warn('Failed to fetch tenant config, using fallback:', error);
    return getFallbackTenantConfig(slug);
  }
}

/**
 * Get tenant configuration by ID
 * This is a simplified version that tries to use the slug as ID
 */
export function getTenantById(id: string): TenantConfig | null {
  console.warn('getTenantById is deprecated. Use companyService.getCompanyBySlug() instead.');
  // Return null to indicate this should be handled by the new service
  return null;
}

/**
 * Get all available tenants
 * This would need to be implemented as an API call in the real system
 */
export function getAllTenants(): TenantConfig[] {
  console.warn('getAllTenants is deprecated. Implement as API call if needed.');
  return [];
}

/**
 * Extract CSS custom properties from tenant configuration
 */
export function getTenantThemeVars(config: TenantConfig): Record<string, string> {
  return {
    '--primary-color': config.primaryColor,
    '--secondary-color': config.secondaryColor,
    '--accent-color': config.accentColor,
  };
}

/**
 * Apply tenant theme to the document
 */
export function applyTenantTheme(config: TenantConfig): void {
  if (typeof document === 'undefined') return;

  try {
    applyTheme(config);
  } catch (error) {
    console.error('Failed to apply tenant theme:', error);
  }
}

/**
 * Generate tenant configuration for a new company
 */
export function generateTenantConfig(input: {
  name: string;
  slug?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
}): Partial<TenantConfig> {
  const slug = input.slug || input.name
    .toLowerCase()
    .replace(/[áàäâ]/g, 'a')
    .replace(/[éèëê]/g, 'e')
    .replace(/[íìïî]/g, 'i')
    .replace(/[óòöô]/g, 'o')
    .replace(/[úùüû]/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return {
    name: input.name,
    slug,
    logo: `/assets/${slug}/logo.svg`,
    favicon: `/assets/${slug}/favicon.ico`,
    primaryColor: input.primaryColor || '#7c3aed',
    secondaryColor: input.secondaryColor || '#a855f7',
    accentColor: input.accentColor || '#06b6d4',
    contact: {
      phone: '',
      email: '',
      address: '',
      whatsapp: ''
    },
    social: {},
    seo: {
      title: `${input.name} - Real Estate`,
      description: `Find your perfect property with ${input.name}`,
      keywords: ['real estate', 'properties'],
      ogImage: `/assets/${slug}/og-image.jpg`
    },
    features: {
      blog: true,
      multiLanguage: false,
      propertyComparison: true,
      virtualTours: true
    },
    currencies: ['USD'],
    languages: ['es'],
    theme: {
      mode: 'light',
      style: 'modern'
    }
  };
}

/**
 * Validate tenant configuration
 */
export function validateTenantConfig(config: unknown): config is TenantConfig {
  if (!config || typeof config !== 'object') return false;

  const tenant = config as any;

  return (
    typeof tenant.id === 'string' &&
    typeof tenant.name === 'string' &&
    typeof tenant.slug === 'string' &&
    typeof tenant.primaryColor === 'string' &&
    typeof tenant.secondaryColor === 'string' &&
    typeof tenant.accentColor === 'string' &&
    tenant.contact && typeof tenant.contact === 'object' &&
    tenant.seo && typeof tenant.seo === 'object' &&
    tenant.features && typeof tenant.features === 'object' &&
    Array.isArray(tenant.currencies) &&
    Array.isArray(tenant.languages) &&
    tenant.theme && typeof tenant.theme === 'object'
  );
}

/**
 * Get tenant color palette
 */
export function getTenantColors(config: TenantConfig): {
  primary: string;
  secondary: string;
  accent: string;
} {
  return {
    primary: config.primaryColor,
    secondary: config.secondaryColor,
    accent: config.accentColor,
  };
}

/**
 * Check if tenant supports a feature
 */
export function tenantSupportsFeature(
  config: TenantConfig,
  feature: keyof TenantConfig['features']
): boolean {
  return config.features[feature] === true;
}

/**
 * Get tenant's primary language
 */
export function getTenantPrimaryLanguage(config: TenantConfig): string {
  return config.languages[0] || 'es';
}

/**
 * Get tenant's primary currency
 */
export function getTenantPrimaryCurrency(config: TenantConfig): string {
  return config.currencies[0] || 'USD';
}